import React, { useEffect, useState, useCallback,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserOnline } from "../../../services/storage/LocalStorage";
import {
    setBannerVisibility,
    setCounter,
} from "../../../state/actions/offlineBanner";
import OfflineBanner from "./OfflineBanner";
import SlowNetworkBanner from "./SlowNetworkBanner";

let intervalId;

const Banner = () => {
    const dispatch = useDispatch();
    const [isUserOnline, setIsUserOnline] = useState(false);
    const [slowInternet, setSlowInternet] = useState(false);
    const { show } = useSelector((state) => state.appNotification);
    const latestAPIRequest = useSelector((state) => state.offlineBanner.latestAPIRequest);
    const counter = useSelector((state) => state.offlineBanner.counter);
    const visibility = useSelector((state) => state.offlineBanner.visibility);
    const intervalRef = useRef(null);

    const refreshImages = () => {
        const images = document.querySelectorAll("img");
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src) {
                img.src = src.split("?")[0] + `?t=${Date.now()}`;
            }
        });
    };

    const checkSlowConnection = useCallback(() => {
        const connection = navigator.connection;
        if (connection && typeof connection.downlink === "number") {
            if (connection.downlink < 0.5) {
                setSlowInternet(true);
                dispatch(setBannerVisibility(false))
            } else {
                setSlowInternet(false);
                dispatch(setBannerVisibility(false))
            }
        }
    }, [dispatch]);

    const changeNetworkHandler = useCallback(() => {
        checkSlowConnection();
    }, [checkSlowConnection]);

    const resetCounter = useCallback((visibility, value) => {
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(tick, 1000);
        dispatch(setBannerVisibility(visibility))
            .then(() => {
                return dispatch(setCounter(value));
            })
            .then(() => {
                countdown();
            });
    }, [dispatch]);

    const checkConnection = useCallback(() => {
        if (isUserOnline) {
            if (latestAPIRequest) {
                const {
                    url,
                    request,
                    responseType,
                    requestSerializeFn,
                    responseDeserializeFn,
                    options = {},
                    noSpinner,
                } = latestAPIRequest;

                // Optionally re-fire the last request
            }
            clearInterval(intervalRef.current);
            dispatch(setBannerVisibility(false));
            setUserOnline(true);
        } else {
            resetCounter(true, 5);
            dispatch(setBannerVisibility(true));
            setUserOnline(false);
        }
    }, [isUserOnline, latestAPIRequest, dispatch, resetCounter]);

    const tick = useCallback(() => {
        if (counter === 0) {
            checkConnection();
        }
        dispatch(setCounter(counter - 1));
    }, [counter, checkConnection, dispatch]);

    const countdown = () => {
        clearInterval(intervalRef.current);
    intervalRef.current = setInterval(tick, 1000);
    };

    const handleCloseBanner = () => {
        clearTimeout(intervalRef.current);
        resetCounter(false, 5);
    };

    const closeSlowInternetBanner = () => {
        dispatch(setBannerVisibility(true));
        setSlowInternet(false);
    };

    useEffect(() => {
        const checkActualConnection = async () => {
            if (!navigator.onLine) {
                setIsUserOnline(false);
                return;
            }

            // Use a timeout so the check doesn't hang indefinitely on severely throttled connections
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 seconds timeout

            try {
                // Ping a highly available external resource bypassing local Service Workers or Dev Servers.
                // 'no-cors' prevents CORS errors while still verifying outbound network reachability.
                await fetch("https://www.cloudflare.com/favicon.ico?_t=" + Date.now(), { 
                    method: "HEAD", 
                    mode: "no-cors", 
                    cache: "no-store",
                    signal: controller.signal
                });
                setIsUserOnline(true);
            } catch (error) {
                setIsUserOnline(false);
            } finally {
                clearTimeout(timeoutId);
            }
        };

        const handleOnline = () => checkActualConnection();
        const handleOffline = () => setIsUserOnline(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        // Run on initial load to fix the "first time it is not working" issue
        checkActualConnection();

        // Polling fallback for Windows: periodically check connection to catch silent drops
        const pollInterval = setInterval(checkActualConnection, 5000);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            clearInterval(pollInterval);
        };
    }, []);

    useEffect(() => {
        checkSlowConnection();

        if (navigator.connection) {
            navigator.connection.addEventListener("change", changeNetworkHandler);
        }

        return () => {
            clearInterval(intervalRef.current);
            if (navigator.connection) {
                navigator.connection.removeEventListener("change", changeNetworkHandler);
            }
        };
    }, [checkSlowConnection, changeNetworkHandler]);

    useEffect(() => {
        if (isUserOnline) {
            refreshImages();
            clearInterval(intervalRef.current);
            dispatch(setBannerVisibility(false)).then(() => {
                dispatch(setCounter(5));
            });
            setUserOnline(true);
            window.dispatchEvent(new Event("stream_closed"));
            return;
        }

        dispatch(setBannerVisibility(true));
        resetCounter(true, 5);
        setUserOnline(false);
    }, [isUserOnline, dispatch, resetCounter]);

    useEffect(() => {
        const disableButtons = () => {
            const trash = document.getElementsByClassName("mapbox-gl-draw_trash");
            const polygon = document.getElementsByClassName("mapbox-gl-draw_polygon");
            if (visibility) {
                if (trash.length) trash[0].setAttribute("disabled", "");
                if (polygon.length) polygon[0].setAttribute("disabled", "");
            } else {
                if (trash.length) trash[0].removeAttribute("disabled");
                if (polygon.length) polygon[0].removeAttribute("disabled");
            }
        };
        disableButtons();
    }, [visibility]);

    if (visibility) return <OfflineBanner />;
    if (show) return null; // Placeholder for app banner
    if (!visibility && slowInternet) {
        return <SlowNetworkBanner closeSlowInternetBanner={closeSlowInternetBanner} />;
    }

    return null;
};

export default Banner;
